import React, { useEffect, useMemo, useRef, useState } from 'react';
import { resolveApiBase } from '@/utils/apiBase';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';
import { Calendar, Settings, GripVertical, X, Building2, FileText, MessageSquare, Trophy } from 'lucide-react';
import { Role } from '@/types';
import { SNAPSHOT_CHARTS, SNAPSHOT_WIDGETS, type SnapshotWidgetId } from '@/config/snapshot_cards';

interface DashboardProps {
  darkMode: boolean;
  role: Role;
}

type AttendanceKpis = {
  range: { from: string; to: string };
  slaSeconds: number;
  firstResponse: { avgSeconds: number; withinSlaPct: number | null; withinSlaCount: number; respondedCount: number };
  resolution: { avgSeconds: number };
  backlog: { total: number };
  closed: { total: number };
  discard: { total: number; rate: number | null };
};

const CORE_API = resolveApiBase(import.meta.env.VITE_CORE_API, '/core', 'http://localhost:3001');
const SNAPSHOT_WIDGET_SCOPE = 'snapshot-cards';

const getAuthHeaders = (): Record<string, string> => {
  if (typeof window === 'undefined') return {};
  const rawToken = localStorage.getItem('va_token') || '';
  return rawToken ? { Authorization: rawToken.startsWith('Bearer ') ? rawToken : `Bearer ${rawToken}` } : {};
};

const toDateOnlyString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getLastNDaysRange = (days: number) => {
  const today = new Date();
  const dayMs = 24 * 60 * 60 * 1000;
  const to = toDateOnlyString(today);
  const from = toDateOnlyString(new Date(today.getTime() - (days - 1) * dayMs));
  return { from, to };
};

const diffDays = (from?: string | null, to?: string | null): number => {
  if (!from || !to) return 7;
  const fromDate = new Date(from);
  const toDate = new Date(to);
  if (Number.isNaN(fromDate.getTime()) || Number.isNaN(toDate.getTime())) return 7;
  const delta = Math.round((toDate.getTime() - fromDate.getTime()) / (24 * 60 * 60 * 1000));
  return Math.max(1, delta);
};

type WidgetDatePreset = 'one_day' | 'week' | 'month' | 'custom';

const getPresetRange = (preset: WidgetDatePreset) => {
  const today = new Date();
  const dayMs = 24 * 60 * 60 * 1000;
  const end = toDateOnlyString(today);
  if (preset === 'one_day') {
    const from = toDateOnlyString(new Date(today.getTime() - dayMs));
    return { from, to: end };
  }
  if (preset === 'week') {
    const offset = 6;
    const from = toDateOnlyString(new Date(today.getTime() - offset * dayMs));
    return { from, to: end };
  }
  if (preset === 'month') {
    const from = toDateOnlyString(new Date(today.getFullYear(), today.getMonth(), 1));
    return { from, to: end };
  }
  return getLastNDaysRange(7);
};

const formatDurationShort = (seconds?: number | null) => {
  if (seconds === null || seconds === undefined) return '--';
  const safe = Math.max(0, Number(seconds));
  if (!Number.isFinite(safe)) return '--';
  if (safe < 60) return `${Math.round(safe)}s`;
  const totalMinutes = Math.round(safe / 60);
  if (totalMinutes < 60) return `${totalMinutes}m`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
};

const formatPercent = (value?: number | null) => {
  if (value === null || value === undefined) return '--';
  if (!Number.isFinite(value)) return '--';
  return `${Math.round(value * 100)}%`;
};

export const DashboardPage: React.FC<DashboardProps> = ({ darkMode, role }) => {
  const [cardSettingsOpen, setCardSettingsOpen] = useState(false);
  const [cardPrefs, setCardPrefs] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(SNAPSHOT_WIDGETS.map((card) => [card.id, true]))
  );
  const [cardOrder, setCardOrder] = useState<SnapshotWidgetId[]>(() => SNAPSHOT_WIDGETS.map((card) => card.id));
  const [draggingCardId, setDraggingCardId] = useState<SnapshotWidgetId | null>(null);
  const [dragOverCardId, setDragOverCardId] = useState<SnapshotWidgetId | null>(null);
  const [draggingCard, setDraggingCard] = useState(false);
  const [snapshotCardsReady, setSnapshotCardsReady] = useState(false);
  const [snapshotCardsSaving, setSnapshotCardsSaving] = useState(false);
  const [snapshotOrderDirty, setSnapshotOrderDirty] = useState(false);
  const [widgetDatePreset, setWidgetDatePreset] = useState<WidgetDatePreset>('custom');
  const [widgetDateFrom, setWidgetDateFrom] = useState<string>(() => getLastNDaysRange(7).from);
  const [widgetDateTo, setWidgetDateTo] = useState<string>(() => getLastNDaysRange(7).to);
  const [draftDatePreset, setDraftDatePreset] = useState<WidgetDatePreset>('custom');
  const [draftDateFrom, setDraftDateFrom] = useState<string>(() => getLastNDaysRange(7).from);
  const [draftDateTo, setDraftDateTo] = useState<string>(() => getLastNDaysRange(7).to);
  const [dateControlsOpen, setDateControlsOpen] = useState(false);
  const dateFromRef = useRef<HTMLInputElement | null>(null);
  const dateToRef = useRef<HTMLInputElement | null>(null);

  const [coordinationSummary, setCoordinationSummary] = useState({
    analysis: 0,
    credentialing: 0,
    active: 0,
    blocked: 0,
    total: 0,
  });
  const [coordinationLiveSummary, setCoordinationLiveSummary] = useState({
    analysis: 0,
    credentialing: 0,
    active: 0,
    blocked: 0,
    total: 0,
  });
  const [pipelineSnapshot, setPipelineSnapshot] = useState<any | null>(null);
  const [pipelineCompare, setPipelineCompare] = useState<any | null>(null);
  const [pipelineSnapshotDate, setPipelineSnapshotDate] = useState<string | null>(null);
  const [pipelineCompareDate, setPipelineCompareDate] = useState<string | null>(null);
  const [pipelineDelta, setPipelineDelta] = useState<Record<string, { now: number; prev: number; diff: number; pct: number | null }> | null>(null);
  const [pipelineSeries, setPipelineSeries] = useState<Array<Record<string, any>>>([]);
  const [pipelineSnapshotLoading, setPipelineSnapshotLoading] = useState(false);
  const [pipelineSnapshotError, setPipelineSnapshotError] = useState<string | null>(null);
  const [attendanceKpis, setAttendanceKpis] = useState<AttendanceKpis | null>(null);
  const [attendanceKpisLoading, setAttendanceKpisLoading] = useState(false);
  const [attendanceKpisError, setAttendanceKpisError] = useState<string | null>(null);
  const [engagementSnapshot, setEngagementSnapshot] = useState<{
    snapshotDate: string | null;
    range: { from: string | null; to: string | null };
    engajamento: {
      imobiliariasTotal: number;
      chamadosTotal: number;
      mediaChamadosPorImobiliaria: number;
      categoriaMaisFrequente: { id: number | null; nome: string | null; total: number };
    };
  } | null>(null);
  const [engagementLoading, setEngagementLoading] = useState(false);
  const [engagementError, setEngagementError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPipelineSnapshots = async () => {
      const compareDays = diffDays(widgetDateFrom, widgetDateTo);
      const baseDate = widgetDateTo || toDateOnlyString(new Date());
      setPipelineSnapshotLoading(true);
      try {
        const response = await fetch(`${CORE_API}/dashboard/pipeline-view?from=${widgetDateFrom}&to=${baseDate}&compareDays=${compareDays}`, {
          headers: { ...getAuthHeaders() },
        });
        if (!response.ok) throw new Error('Falha ao carregar indicadores de credenciamento');
        const data = await response.json();
        const snapshot = data?.current || null;
        const compare = data?.previous || null;
        const snapshotDate = data?.snapshotDate || null;
        const compareDate = data?.compareDate || null;
        const delta = data?.delta || null;
        const series = Array.isArray(data?.series) ? data.series : [];
        const error = snapshot ? null : 'Snapshot nao encontrado para a data solicitada.';
        setPipelineSnapshot(snapshot);
        setPipelineCompare(compare);
        setPipelineSnapshotDate(snapshotDate);
        setPipelineCompareDate(compareDate);
        setPipelineDelta(delta);
        setPipelineSeries(series);
        setPipelineSnapshotError(error);
      } catch (err) {
        console.error('Erro ao buscar indicadores de credenciamento', err);
        setPipelineSnapshot(null);
        setPipelineCompare(null);
        setPipelineSnapshotDate(null);
        setPipelineCompareDate(null);
        setPipelineDelta(null);
        setPipelineSeries([]);
        setPipelineSnapshotError('Falha ao carregar os indicadores de credenciamento.');
      } finally {
        setPipelineSnapshotLoading(false);
      }
    };

    const fetchCoordinationSummary = async () => {
      try {
        const response = await fetch(`${CORE_API}/dashboard/coordination-summary?date=${widgetDateTo}`, {
          headers: { ...getAuthHeaders() },
        });
        if (!response.ok) throw new Error('Falha ao carregar resumo de credenciamento');
        const data = await response.json();
        const summary = data?.summary || {};
        setCoordinationSummary({
          analysis: Number(summary.analysis) || 0,
          credentialing: Number(summary.credentialing) || 0,
          active: Number(summary.active) || 0,
          blocked: Number(summary.blocked) || 0,
          total: Number(summary.total) || 0,
        });
      } catch (err) {
        console.error('Erro ao carregar resumo de credenciamento', err);
        setCoordinationSummary({ analysis: 0, credentialing: 0, active: 0, blocked: 0, total: 0 });
      }
    };

    const fetchCoordinationLive = async () => {
      try {
        const response = await fetch(`${CORE_API}/dashboard/coordination-live`, {
          headers: { ...getAuthHeaders() },
        });
        if (!response.ok) throw new Error('Falha ao carregar resumo live de credenciamento');
        const data = await response.json();
        const summary = data?.summary || {};
        console.debug('[dashboard] coordination-live raw', data);
        console.debug('[dashboard] coordination-live summary', {
          analysis: Number(summary.analysis) || 0,
          credentialing: Number(summary.credentialing) || 0,
          active: Number(summary.active) || 0,
          blocked: Number(summary.blocked) || 0,
          total: Number(summary.total) || 0,
        });
        setCoordinationLiveSummary({
          analysis: Number(summary.analysis) || 0,
          credentialing: Number(summary.credentialing) || 0,
          active: Number(summary.active) || 0,
          blocked: Number(summary.blocked) || 0,
          total: Number(summary.total) || 0,
        });
      } catch (err) {
        console.error('Erro ao carregar resumo live de credenciamento', err);
        setCoordinationLiveSummary({ analysis: 0, credentialing: 0, active: 0, blocked: 0, total: 0 });
      }
    };

    const fetchAttendance = async () => {
      setAttendanceKpisLoading(true);
      setAttendanceKpisError(null);
      try {
        const range = getLastNDaysRange(7);
        const baseDate = widgetDateTo || range.to;
        const params = new URLSearchParams({
          from: range.from,
          to: baseDate,
          slaSeconds: '3600',
        });
        const response = await fetch(`${CORE_API}/dashboard/atendimento-kpis?${params.toString()}`, {
          headers: { ...getAuthHeaders() },
        });
        if (!response.ok) throw new Error('Falha ao carregar KPIs de atendimento');
        const data = await response.json();
        setAttendanceKpis(data);
      } catch (err) {
        console.error('Erro ao carregar KPIs de atendimento', err);
        setAttendanceKpis(null);
        setAttendanceKpisError('Falha ao carregar KPIs de atendimento.');
      } finally {
        setAttendanceKpisLoading(false);
      }
    };

    const fetchEngagement = async () => {
      setEngagementLoading(true);
      setEngagementError(null);
      try {
        const params = new URLSearchParams({
          scope: 'tenant',
          from: widgetDateFrom,
          to: widgetDateTo,
        });
        const response = await fetch(`${CORE_API}/dashboard/engajamento?${params.toString()}`, {
          headers: { ...getAuthHeaders() },
        });
        if (!response.ok) throw new Error('Falha ao carregar engajamento');
        const data = await response.json();
        setEngagementSnapshot(data);
      } catch (err) {
        console.error('Erro ao carregar engajamento', err);
        setEngagementSnapshot(null);
        setEngagementError('Falha ao carregar engajamento.');
      } finally {
        setEngagementLoading(false);
      }
    };

    fetchPipelineSnapshots();
    fetchCoordinationSummary();
    fetchCoordinationLive();
    fetchAttendance();
    fetchEngagement();
  }, [widgetDateFrom, widgetDateTo]);

  const isCoordinationToday = useMemo(() => {
    const selectedDate = widgetDateTo || toDateOnlyString(new Date());
    return selectedDate === toDateOnlyString(new Date());
  }, [widgetDateTo]);

  const coordinationDisplaySummary = useMemo(() => (
    isCoordinationToday ? coordinationLiveSummary : coordinationSummary
  ), [coordinationLiveSummary, coordinationSummary, isCoordinationToday]);

  const coordinationStatusTotals = useMemo(() => ({
    analysis: coordinationDisplaySummary.analysis,
    credentialing: coordinationDisplaySummary.credentialing,
    active: coordinationDisplaySummary.active,
    blocked: coordinationDisplaySummary.blocked,
  }), [coordinationDisplaySummary]);

  const generalKpis = useMemo(() => {
    if (!pipelineSnapshot || !pipelineDelta) return [];
    const toDelta = (value?: { diff: number }) => {
      const diff = Number(value?.diff || 0);
      if (diff === 0) return { label: 'estavel', tone: 'neutral' as const };
      return diff > 0 ? { label: '+' + diff, tone: 'up' as const } : { label: '' + diff, tone: 'down' as const };
    };

    const conversionRate = Number(pipelineSnapshot.conversao_pct || 0);
    return [
      {
        id: 'entries',
        label: 'Novas imobiliarias (7d)',
        value: Number(pipelineSnapshot.entradas_7d || 0),
        unit: '',
        delta: toDelta(pipelineDelta.entradas_7d),
        helper: 'Entradas no funil',
      },
      {
        id: 'moves',
        label: 'Mudancas de status (7d)',
        value: Number(pipelineSnapshot.mudancas_status_7d || 0),
        unit: '',
        delta: toDelta(pipelineDelta.mudancas_status_7d),
        helper: 'Transicoes no pipeline',
      },
      {
        id: 'approved',
        label: 'Aprovadas (7d)',
        value: Number(pipelineSnapshot.aprovadas_7d || 0),
        unit: '',
        delta: toDelta(pipelineDelta.aprovadas_7d),
        helper: 'Conclusoes positivas',
      },
      {
        id: 'rejected',
        label: 'Suspensas (7d)',
        value: Number(pipelineSnapshot.rejeitadas_7d || 0),
        unit: '',
        delta: toDelta(pipelineDelta.rejeitadas_7d),
        helper: 'Saidas negativas',
      },
      {
        id: 'analysis-time',
        label: 'Tempo medio em analise',
        value: Number(pipelineSnapshot.avg_dias_analise || 0),
        unit: 'dias',
        delta: toDelta(pipelineDelta.avg_dias_analise),
        helper: 'Tempo ate mover de analise',
      },
      {
        id: 'cred-time',
        label: 'Tempo medio em credenciamento',
        value: Number(pipelineSnapshot.avg_dias_credenciamento || 0),
        unit: 'dias',
        delta: toDelta(pipelineDelta.avg_dias_credenciamento),
        helper: 'Tempo ate aprovar ou bloquear',
      },
      {
        id: 'backlog',
        label: 'Backlog acima de 7d',
        value: Number(pipelineSnapshot.backlog_acima_7d || 0),
        unit: '',
        delta: toDelta(pipelineDelta.backlog_acima_7d),
        helper: 'Risco de SLA estourado',
      },
      {
        id: 'conversion',
        label: 'Conversao do funil',
        value: conversionRate,
        unit: '%',
        delta: toDelta(pipelineDelta.conversao_pct),
        helper: 'Aprovadas / total',
        progress: Math.min(100, Math.max(0, conversionRate)),
        progressColor: darkMode ? '#22c55e' : '#16a34a',
      },
    ];
  }, [pipelineSnapshot, pipelineDelta, darkMode]);

  const orderedSnapshotWidgets = useMemo(() => {
    const byId = new Map(SNAPSHOT_WIDGETS.map((card) => [card.id, card]));
    const ordered = cardOrder.map((id) => byId.get(id)).filter(Boolean) as typeof SNAPSHOT_WIDGETS;
    const missing = SNAPSHOT_WIDGETS.filter((card) => !ordered.some((item) => item.id === card.id));
    return [...ordered, ...missing];
  }, [cardOrder]);

  const isSnapshotCardEnabled = useMemo(
    () => (id: SnapshotWidgetId) => {
      const stored = cardPrefs[id];
      if (stored !== undefined) return stored;
      const card = SNAPSHOT_WIDGETS.find((item) => item.id === id);
      return card ? card.active : true;
    },
    [cardPrefs]
  );

  const visibleSnapshotWidgets = orderedSnapshotWidgets.filter((card) => isSnapshotCardEnabled(card.id));

  const generalWidgetOrder = useMemo(() => {
    const order: string[] = [];
    visibleSnapshotWidgets.forEach((widget) => {
      widget.widgetKeys.forEach((key) => {
        order.push(key);
      });
    });
    return order;
  }, [visibleSnapshotWidgets]);

  const datePresetOptions = useMemo(() => ([
    { value: 'one_day', label: '1DIA' },
    { value: 'week', label: 'SEMANA' },
    { value: 'month', label: 'MES' },
    { value: 'custom', label: 'PERSONALIZADO' },
  ]), []);

  const currentPresetLabel = useMemo(() => {
    const match = datePresetOptions.find((option) => option.value === widgetDatePreset);
    return match?.label || 'Personalizado';
  }, [datePresetOptions, widgetDatePreset]);

  const persistSnapshotCardPrefs = React.useCallback(
    async (prefs: Record<string, boolean>) => {
      setSnapshotCardsSaving(true);
      try {
        const cards = orderedSnapshotWidgets.length ? orderedSnapshotWidgets : SNAPSHOT_WIDGETS;
        const payload = {
          scope: SNAPSHOT_WIDGET_SCOPE,
          widgets: cards.map((card, index) => ({
            key: card.id,
            enabled: prefs[card.id] !== undefined ? prefs[card.id] : card.active,
            position: index + 1,
            requiredService: null,
          })),
        };
        const response = await fetch(`${CORE_API}/widgets`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify(payload),
        });
        if (!response.ok) throw new Error('Falha ao salvar cards de snapshot');
      } catch (err) {
        console.error('Erro ao salvar configuracoes de cards de snapshot', err);
      } finally {
        setSnapshotCardsSaving(false);
      }
    },
    [orderedSnapshotWidgets]
  );

  useEffect(() => {
    if (!snapshotOrderDirty) return;
    persistSnapshotCardPrefs(cardPrefs);
    setSnapshotOrderDirty(false);
  }, [snapshotOrderDirty, cardPrefs, persistSnapshotCardPrefs]);

  const loadSnapshotCardPrefs = React.useCallback(async () => {
    setSnapshotCardsReady(false);
    try {
      const params = new URLSearchParams({ scope: SNAPSHOT_WIDGET_SCOPE });
      const response = await fetch(`${CORE_API}/widgets?${params.toString()}`, { headers: { ...getAuthHeaders() } });
      if (!response.ok) throw new Error('Falha ao carregar cards de snapshot');
      const data = await response.json();
      const widgets = Array.isArray(data?.widgets) ? data.widgets : [];
      const prefs: Record<string, boolean> = {};
      widgets.forEach((widget: any) => {
        if (!widget?.key) return;
        prefs[String(widget.key)] = widget.enabled !== undefined ? !!widget.enabled : true;
      });
      if (Object.keys(prefs).length) {
        setCardPrefs(prefs);
      }
      const orderedKeys = widgets
        .filter((widget: any) => widget?.key && widget?.position !== undefined)
        .sort((a: any, b: any) => Number(a.position) - Number(b.position))
        .map((widget: any) => String(widget.key) as SnapshotWidgetId);
      if (orderedKeys.length) {
        const known = new Set(SNAPSHOT_WIDGETS.map((card) => card.id));
        setCardOrder(orderedKeys.filter((key: SnapshotWidgetId) => known.has(key)));
      }
    } catch (err) {
      console.error('Erro ao carregar configuracoes de cards de snapshot', err);
    } finally {
      setSnapshotCardsReady(true);
    }
  }, []);

  useEffect(() => {
    loadSnapshotCardPrefs();
  }, [loadSnapshotCardPrefs]);

  const toggleCard = (id: SnapshotWidgetId) => {
    setCardPrefs((prev) => {
      const next = { ...prev, [id]: !(prev[id] ?? true) };
      persistSnapshotCardPrefs(next);
      return next;
    });
  };

  const moveCard = (fromId: SnapshotWidgetId, toId: SnapshotWidgetId) => {
    if (fromId === toId) return;
    setCardOrder((prev) => {
      const next = prev.length ? [...prev] : SNAPSHOT_WIDGETS.map((card) => card.id);
      const fromIndex = next.indexOf(fromId);
      const toIndex = next.indexOf(toId);
      if (fromIndex === -1 || toIndex === -1) return prev;
      next.splice(fromIndex, 1);
      next.splice(toIndex, 0, fromId);
      return next;
    });
    setSnapshotOrderDirty(true);
  };

  const handlePresetChange = (value: WidgetDatePreset) => {
    setDraftDatePreset(value);
    if (value === 'custom') return;
    const range = getPresetRange(value);
    setDraftDateFrom(range.from);
    setDraftDateTo(range.to);
  };

  const handleDateFromChange = (value: string) => {
    setDraftDatePreset('custom');
    setDraftDateFrom(value);
    setDraftDateTo(value);
  };

  const handleDateToChange = (value: string) => {
    setDraftDatePreset('custom');
    setDraftDateTo(value);
    if (draftDateFrom && value < draftDateFrom) {
      setDraftDateFrom(value);
    }
  };

  const applyWidgetDateRange = () => {
    setWidgetDatePreset(draftDatePreset);
    setWidgetDateFrom(draftDateFrom);
    setWidgetDateTo(draftDateTo);
    setDateControlsOpen(false);
  };

  const openDateControls = () => {
    setDraftDatePreset(widgetDatePreset);
    setDraftDateFrom(widgetDateFrom);
    setDraftDateTo(widgetDateTo);
    setDateControlsOpen(true);
  };

  const renderWidgetDateControls = () => {
    if (!dateControlsOpen) return null;

    const openDatePicker = (ref: React.MutableRefObject<HTMLInputElement | null>) => {
      const input = ref.current;
      if (!input || input.disabled) return;
      if (typeof (input as any).showPicker === 'function') {
        (input as any).showPicker();
      } else {
        input.focus();
      }
    };

    const maxDate = toDateOnlyString(new Date());

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
        <div className={`w-full max-w-lg rounded-2xl border shadow-xl ${darkMode ? 'bg-vision-darkGreen border-vision-border text-gray-100' : 'bg-white border-gray-200 text-gray-900'}`}>
          <div className={`flex items-center justify-between p-4 border-b ${darkMode ? 'border-vision-border' : 'border-gray-200'}`}>
            <div className="text-center flex-1">
              <p className="text-sm font-semibold">Periodo do dashboard</p>
              <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Defina o intervalo para calcular os KPIs do dashboard.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setDateControlsOpen(false)}
              className={`p-2 rounded-lg border transition-colors ${
                darkMode
                  ? 'border-vision-border text-gray-200 hover:bg-vision-surface'
                  : 'border-gray-200 text-gray-700 hover:bg-gray-100'
              }`}
              aria-label="Fechar"
            >
              <X size={16} />
            </button>
          </div>

          <div className="p-5">
            <div className="grid grid-cols-2 gap-2">
              {datePresetOptions.map((preset) => (
                <button
                  key={preset.value}
                  type="button"
                  onClick={() => handlePresetChange(preset.value as WidgetDatePreset)}
                  className={`px-3 py-2 rounded-lg text-xs font-semibold border transition-colors ${
                    draftDatePreset === preset.value
                      ? darkMode
                        ? 'bg-vision-activeRed border-vision-activeRed text-white'
                        : 'bg-[#294b29] border-[#294b29] text-white'
                      : darkMode
                        ? 'border-vision-border hover:bg-vision-surface'
                        : 'border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  {preset.label}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
              <div className="space-y-2">
                <label className={`text-xs font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Data inicial</label>
                <button
                  type="button"
                  onClick={() => openDatePicker(dateFromRef)}
                  className={`w-full text-left px-3 py-2 rounded-lg border text-sm ${
                    darkMode
                      ? 'border-vision-border bg-vision-surface text-gray-100'
                      : 'border-gray-200 bg-white text-gray-800'
                  }`}
                >
                  {draftDateFrom || '--'}
                </button>
                <input
                  ref={dateFromRef}
                  type="date"
                  value={draftDateFrom}
                  max={maxDate}
                  onChange={(event) => handleDateFromChange(event.target.value)}
                  className="hidden"
                />
              </div>
              <div className="space-y-2">
                <label className={`text-xs font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Data final</label>
                <button
                  type="button"
                  onClick={() => openDatePicker(dateToRef)}
                  className={`w-full text-left px-3 py-2 rounded-lg border text-sm ${
                    darkMode
                      ? 'border-vision-border bg-vision-surface text-gray-100'
                      : 'border-gray-200 bg-white text-gray-800'
                  }`}
                >
                  {draftDateTo || '--'}
                </button>
                <input
                  ref={dateToRef}
                  type="date"
                  value={draftDateTo}
                  max={maxDate}
                  onChange={(event) => handleDateToChange(event.target.value)}
                  className="hidden"
                />
              </div>
            </div>

            <div className="flex items-center justify-between mt-6">
              <button
                type="button"
                onClick={() => setDateControlsOpen(false)}
                className={`px-3 py-2 rounded-lg text-xs font-semibold border transition-colors ${
                  darkMode
                    ? 'border-vision-border text-gray-200 hover:bg-vision-surface'
                    : 'border-gray-200 text-gray-700 hover:bg-gray-100'
                }`}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={applyWidgetDateRange}
                className={`px-4 py-2 rounded-lg text-xs font-semibold text-white transition-colors ${
                  darkMode ? 'bg-vision-activeRed hover:bg-red-700' : 'bg-[#294b29] hover:bg-[#1a301a]'
                }`}
              >
                Aplicar
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderGeneralWidget = (key: string) => {
    if (key === 'coordination') {
      return (
        <div key={key} className={`p-5 rounded-xl border shadow-sm ${darkMode ? 'bg-vision-darkGreen border-vision-border' : 'bg-white border-gray-200'}`}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="font-bold flex items-center gap-2">
                <Building2 size={18} /> Credenciamento
              </h3>
              <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Panorama das imobiliarias por status.
              </p>
            </div>
            <div className={`${darkMode ? 'bg-vision-surface text-gray-100 border border-vision-border' : 'bg-gray-100 text-gray-700 border border-gray-200'} px-3 py-1 rounded-full text-xs font-semibold`}>
              Total: {coordinationDisplaySummary.total}
            </div>
          </div>

          <div className="grid lg:grid-cols-12 gap-4 mt-4">
            {(() => {
              const trendCard = SNAPSHOT_CHARTS.find((card) => card.id === 'snapshot_trend');
              const distributionCard = SNAPSHOT_CHARTS.find((card) => card.id === 'snapshot_distribution');

              const palette = {
                analysis: darkMode ? '#60a5fa' : '#3b82f6',
                credentialing: darkMode ? '#facc15' : '#eab308',
                active: darkMode ? '#22c55e' : '#16a34a',
                blocked: darkMode ? '#ef4444' : '#dc2626',
                glow: darkMode ? 'rgba(239, 68, 68, 0.35)' : 'rgba(239, 68, 68, 0.2)',
                grid: darkMode ? '#223022' : '#e5e7eb',
                axis: darkMode ? '#9ca3af' : '#6b7280',
              };

              const snapshotTotals = pipelineSnapshot
                ? {
                    analysis: Number(pipelineSnapshot.em_analise || 0),
                    credentialing: Number(pipelineSnapshot.credenciando || 0),
                    active: Number(pipelineSnapshot.aprovadas || 0),
                    blocked: Number(pipelineSnapshot.suspensas || 0),
                  }
                : null;
              const liveTotals = {
                analysis: coordinationStatusTotals.analysis,
                credentialing: coordinationStatusTotals.credentialing,
                active: coordinationStatusTotals.active,
                blocked: coordinationStatusTotals.blocked,
              };
              const hasLiveTotals =
                liveTotals.analysis + liveTotals.credentialing + liveTotals.active + liveTotals.blocked > 0;
              const lastTotals = hasLiveTotals ? liveTotals : snapshotTotals;
              const todayLabel = toDateOnlyString(new Date());
              let trendData = pipelineSeries.map((item: any) => {
                const emAnalise = Number(item?.em_analise ?? 0);
                const credenciando = Number(item?.credenciando ?? 0);
                const aprovadas = Number(item?.aprovadas ?? 0);
                const suspensas = Number(item?.suspensas ?? 0);
                const hasData = item?.hasData !== false;
                return {
                  date: String(item?.date || ''),
                  em_analise: hasData ? emAnalise : null,
                  credenciando: hasData ? credenciando : null,
                  aprovadas: hasData ? aprovadas : null,
                  Suspensas: hasData ? suspensas : null,
                  gargalo: hasData ? (emAnalise + credenciando) : null,
                };
              });
              if (trendData.length && lastTotals) {
                const lastIndex = trendData.length - 1;
                const lastSeries = pipelineSeries[lastIndex];
                const lastPoint = trendData[lastIndex];
                const missingValues = [
                  lastPoint?.em_analise,
                  lastPoint?.credenciando,
                  lastPoint?.aprovadas,
                  lastPoint?.Suspensas,
                ].some((value) => value === null || value === undefined);
                const shouldReplace =
                  lastSeries?.hasData === false ||
                  lastPoint?.date === todayLabel ||
                  missingValues;
                if (shouldReplace) {
                  trendData[lastIndex] = {
                    ...lastPoint,
                    date: lastPoint?.date || todayLabel,
                    em_analise: lastTotals.analysis,
                    credenciando: lastTotals.credentialing,
                    aprovadas: lastTotals.active,
                    Suspensas: lastTotals.blocked,
                    gargalo: lastTotals.analysis + lastTotals.credentialing,
                  };
                }
              }

              const visibleCount = 7;
              const totalPeriods = trendData.length;
              const endValue = Math.max(0, totalPeriods - 1);
              const startValue = Math.max(0, totalPeriods - visibleCount);

              const statusDistribution = [
                { name: 'Analise', value: coordinationStatusTotals.analysis },
                { name: 'Credenciando', value: coordinationStatusTotals.credentialing },
                { name: 'Ativas', value: coordinationStatusTotals.active },
                { name: 'Suspensas', value: coordinationStatusTotals.blocked },
              ];

              const totalDistribution = statusDistribution.reduce((sum, item) => sum + item.value, 0);

              const trendOption = {
                backgroundColor: 'transparent',
                tooltip: {
                  trigger: 'axis',
                  formatter: (params: any) => {
                    if (!Array.isArray(params)) return '';
                    const order = ['Suspensas', 'Analise', 'Credenciando', 'Ativas'];
                    const byName = new Map(params.map((item) => [item.seriesName, item]));
                    const rows = order
                      .map((name) => byName.get(name))
                      .filter(Boolean)
                      .map((item: any) => `${item.marker}${item.seriesName}: ${item.value}`)
                      .join('<br/>');
                    const rawDate = params[0]?.axisValueLabel || '';
                    const formattedDate = rawDate && /^\d{4}-\d{2}-\d{2}$/.test(rawDate)
                      ? rawDate.split('-').reverse().join('/')
                      : rawDate;
                    const title = formattedDate ? `${formattedDate}<br/>` : '';
                    const total = order.reduce((sum, name) => sum + (Number(byName.get(name)?.value) || 0), 0);
                    const totalLine = `<br/>Total: ${total}`;
                    return `${title}${rows}${totalLine}`;
                  }
                },
                grid: { left: 12, right: 12, top: 24, bottom: 34, containLabel: true },
                xAxis: {
                  type: 'category',
                  data: trendData.map((period) => period.date),
                  axisLine: { lineStyle: { color: palette.grid } },
                  axisLabel: { color: palette.axis, fontSize: 10 },
                  axisTick: { show: false },
                },
                yAxis: {
                  type: 'value',
                  axisLine: { show: false },
                  splitLine: { lineStyle: { color: palette.grid, opacity: 0.35 } },
                  axisLabel: { color: palette.axis, fontSize: 10 },
                },
                dataZoom: [
                  {
                    type: 'inside',
                    xAxisIndex: 0,
                    startValue,
                    endValue,
                    moveOnMouseWheel: true,
                    moveOnMouseMove: true,
                  },
                  {
                    type: 'slider',
                    xAxisIndex: 0,
                    height: 14,
                    bottom: 6,
                    startValue,
                    endValue,
                    backgroundColor: darkMode ? '#1f2a1f' : '#f3f4f6',
                    borderColor: darkMode ? '#2a3a2a' : '#e5e7eb',
                    fillerColor: darkMode ? 'rgba(239, 68, 68, 0.2)' : 'rgba(239, 68, 68, 0.15)',
                    handleStyle: {
                      color: darkMode ? '#ef4444' : '#dc2626',
                      borderColor: darkMode ? '#ef4444' : '#dc2626',
                    },
                    textStyle: { color: palette.axis, fontSize: 10 },
                  },
                ],
                series: [
                  {
                    name: 'Ativas',
                    type: 'bar',
                    data: trendData.map((period) => period.aprovadas),
                    barWidth: 12,
                    stack: 'status',
                    itemStyle: { color: palette.active },
                  },
                  {
                    name: 'Credenciando',
                    type: 'bar',
                    data: trendData.map((period) => period.credenciando),
                    barWidth: 12,
                    stack: 'status',
                    itemStyle: { color: palette.credentialing },
                  },
                  {
                    name: 'Analise',
                    type: 'bar',
                    data: trendData.map((period) => period.em_analise),
                    barWidth: 12,
                    stack: 'status',
                    itemStyle: { color: palette.analysis },
                  },
                  {
                    name: 'Suspensas',
                    type: 'bar',
                    data: trendData.map((period) => period.Suspensas),
                    barWidth: 12,
                    stack: 'status',
                    itemStyle: { color: palette.blocked },
                  },
                  {
                    name: 'Gargalo',
                    type: 'line',
                    data: trendData.map((period) => period.gargalo),
                    smooth: true,
                    symbol: 'circle',
                    symbolSize: 6,
                    lineStyle: { width: 2, type: 'dashed', color: palette.blocked },
                    itemStyle: { color: palette.blocked },
                  },
                ],
              };

              const donutOption = {
                backgroundColor: 'transparent',
                tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
                legend: {
                  bottom: 0,
                  textStyle: { color: palette.axis, fontSize: 10 },
                  itemWidth: 10,
                  itemHeight: 10,
                },
                graphic: [
                  {
                    type: 'text',
                    left: 'center',
                    top: '38%',
                    style: {
                      text: String(totalDistribution || 0),
                      fill: darkMode ? '#f3f4f6' : '#111827',
                      fontSize: 20,
                      fontWeight: 700,
                    },
                  },
                  {
                    type: 'text',
                    left: 'center',
                    top: '52%',
                    style: {
                      text: 'Total',
                      fill: palette.axis,
                      fontSize: 10,
                      fontWeight: 600,
                      letterSpacing: 1,
                    },
                  },
                ],
                series: [
                  {
                    type: 'pie',
                    radius: ['50%', '78%'],
                    center: ['50%', '44%'],
                    padAngle: 2,
                    minAngle: 6,
                    label: { show: false },
                    labelLine: { show: false },
                    itemStyle: {
                      borderWidth: 2,
                      borderRadius: 8,
                      borderColor: darkMode ? '#0f1b12' : '#ffffff',
                      shadowBlur: 12,
                      shadowColor: darkMode ? 'rgba(0, 0, 0, 0.35)' : 'rgba(0, 0, 0, 0.12)',
                    },
                    data: [
                      { value: statusDistribution[0].value, name: statusDistribution[0].name, itemStyle: { color: palette.analysis } },
                      { value: statusDistribution[1].value, name: statusDistribution[1].name, itemStyle: { color: palette.credentialing } },
                      { value: statusDistribution[2].value, name: statusDistribution[2].name, itemStyle: { color: palette.active } },
                      { value: statusDistribution[3].value, name: statusDistribution[3].name, itemStyle: { color: palette.blocked } },
                    ],
                    emphasis: { scale: true, scaleSize: 10 },
                  },
                ],
              };

              return (
                <>
                  {trendCard && (
                    <div className={`lg:col-span-8 rounded-xl border p-3 ${darkMode ? 'border-vision-border bg-vision-surface/40' : 'border-gray-200 bg-gray-50'}`}>
                      <div className="flex items-center justify-between text-xs font-semibold mb-2">
                        <span>{trendCard.title}</span>
                        <span className={`${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{trendCard.subtitle}</span>
                      </div>
                      <ReactECharts option={trendOption} style={{ height: 220 }} />
                    </div>
                  )}
                  {distributionCard && (
                    <div className={`lg:col-span-4 rounded-xl border p-3 ${darkMode ? 'border-vision-border bg-vision-surface/40' : 'border-gray-200 bg-gray-50'}`}>
                      <div className="flex items-center justify-between text-xs font-semibold mb-2">
                        <span>{distributionCard.title}</span>
                        <span className={`${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{distributionCard.subtitle}</span>
                      </div>
                      <ReactECharts option={donutOption} style={{ height: 220 }} />
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      );
    }
    if (key === 'pipeline_kpis') {
      return (
        <div key={key} className={`p-5 rounded-xl border shadow-sm ${darkMode ? 'bg-vision-darkGreen border-vision-border' : 'bg-white border-gray-200'}`}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="font-bold flex items-center gap-2">
                <FileText size={18} /> Indicadores de credenciamento
              </h3>
              {pipelineSnapshotError ? (
                <p className={`text-xs mt-1 ${darkMode ? 'text-red-300' : 'text-red-600'}`}>
                  {pipelineSnapshotError}
                </p>
              ) : pipelineSnapshotLoading ? (
                <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                  Carregando indicadores...
                </p>
              ) : pipelineSnapshotDate ? (
                <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                  Snapshot: {pipelineSnapshotDate}
                </p>
              ) : null}
            </div>
            <div className={`${darkMode ? 'bg-vision-surface text-gray-100 border border-vision-border' : 'bg-gray-100 text-gray-700 border border-gray-200'} px-3 py-1 rounded-full text-xs font-semibold`}>
              Base: {pipelineSnapshot ? Number(pipelineSnapshot.total || 0) : '--'}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
            {generalKpis.length === 0 && !pipelineSnapshotLoading ? (
              <div className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                Indicadores indisponiveis.
              </div>
            ) : null}
            {generalKpis.map((kpi) => {
              const toneClass =
                kpi.delta.tone === 'up'
                  ? (darkMode ? 'text-green-300 bg-green-900/30' : 'text-green-800 bg-green-100')
                  : kpi.delta.tone === 'down'
                  ? (darkMode ? 'text-red-300 bg-red-900/30' : 'text-red-700 bg-red-100')
                  : (darkMode ? 'text-gray-300 bg-vision-surface' : 'text-gray-600 bg-gray-100');

              return (
                <div
                  key={kpi.id}
                  className={`rounded-lg border p-3 ${darkMode ? 'border-vision-border bg-vision-surface/40' : 'border-gray-200 bg-gray-50'}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <p className={`text-xs font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      {kpi.label}
                    </p>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${toneClass}`}>
                      {kpi.delta.label}
                    </span>
                  </div>
                  <div className="mt-2 flex items-end gap-2">
                    <span className="text-2xl font-bold">{kpi.value}</span>
                    {kpi.unit ? <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{kpi.unit}</span> : null}
                  </div>
                  {kpi.helper ? (
                    <p className={`text-[11px] mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>{kpi.helper}</p>
                  ) : null}
                  {kpi.progress !== undefined ? (
                    <div className={`mt-3 h-1.5 rounded-full ${darkMode ? 'bg-vision-darkGreen' : 'bg-gray-200'}`}>
                      <div
                        className="h-1.5 rounded-full"
                        style={{ width: `${kpi.progress}%`, backgroundColor: kpi.progressColor }}
                      />
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    if (key === 'attendance_kpis') {
      const rangeLabel = attendanceKpis
        ? `${attendanceKpis.range.from} a ${attendanceKpis.range.to}`
        : 'Ultimos 7 dias';
      const respondedCount = attendanceKpis?.firstResponse.respondedCount ?? 0;
      const withinSlaCount = attendanceKpis?.firstResponse.withinSlaCount ?? 0;
      const closedTotal = attendanceKpis?.closed.total ?? 0;
      const discardTotal = attendanceKpis?.discard.total ?? 0;
      const discardBase = closedTotal + discardTotal;

      const items = [
        {
          id: 'tfr',
          label: '1a resposta',
          value: formatDurationShort(attendanceKpis?.firstResponse.avgSeconds),
          helper: 'media',
        },
        {
          id: 'sla',
          label: 'SLA 1h',
          value: formatPercent(attendanceKpis?.firstResponse.withinSlaPct),
          helper: respondedCount ? `${withinSlaCount}/${respondedCount}` : 'sem base',
        },
        {
          id: 'resolucao',
          label: 'Tempo de resolucao',
          value: formatDurationShort(attendanceKpis?.resolution.avgSeconds),
          helper: 'media',
        },
        {
          id: 'backlog',
          label: 'Backlog aberto',
          value: attendanceKpis ? String(attendanceKpis.backlog.total) : '--',
        },
        {
          id: 'encerrados',
          label: 'Encerrados (7d)',
          value: attendanceKpis ? String(attendanceKpis.closed.total) : '--',
        },
        {
          id: 'descarte',
          label: 'Taxa de descarte',
          value: formatPercent(attendanceKpis?.discard.rate),
          helper: discardBase ? `${discardTotal}/${discardBase}` : 'sem base',
        },
      ];

      return (
        <div key={key} className={`p-5 rounded-xl border shadow-sm ${darkMode ? 'bg-vision-darkGreen border-vision-border' : 'bg-white border-gray-200'}`}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="font-bold flex items-center gap-2">
                <MessageSquare size={18} /> Atendimentos
              </h3>
              {attendanceKpisError ? (
                <p className={`text-xs mt-1 ${darkMode ? 'text-red-300' : 'text-red-600'}`}>
                  {attendanceKpisError}
                </p>
              ) : attendanceKpisLoading ? (
                <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                  Carregando KPIs...
                </p>
              ) : (
                <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                  Periodo: {rangeLabel}
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
            {items.map((item) => (
              <div
                key={item.id}
                className={`rounded-lg border p-3 ${darkMode ? 'border-vision-border bg-vision-surface/40' : 'border-gray-200 bg-gray-50'}`}
              >
                <p className={`text-xs font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {item.label}
                </p>
                <div className="mt-2 flex items-end gap-2">
                  <span className="text-2xl font-bold">{item.value}</span>
                </div>
                {item.helper ? (
                  <p className={`text-[11px] mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>{item.helper}</p>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (key === 'engagement_kpis') {
      const snapshot = engagementSnapshot?.engajamento;
      const totalAgencies = snapshot?.imobiliariasTotal ?? 0;
      const totalTickets = snapshot?.chamadosTotal ?? 0;
      const avgTickets = snapshot
        ? String(snapshot.mediaChamadosPorImobiliaria ?? 0).replace('.', ',')
        : '0,0';
      const topCategory = snapshot?.categoriaMaisFrequente?.nome || 'Sem dados';

      const stats = [
        { id: 'agencies', label: 'Imobiliarias', value: String(totalAgencies), helper: 'Total' },
        { id: 'tickets', label: 'Chamados', value: String(totalTickets), helper: 'Total' },
        { id: 'avg', label: 'Media por imobiliaria', value: avgTickets, helper: 'Chamados' },
        { id: 'top', label: 'Top categoria', value: topCategory, helper: 'Ranking' },
      ];

      return (
        <div key={key} className={`p-5 rounded-xl border shadow-sm ${darkMode ? 'bg-vision-darkGreen border-vision-border' : 'bg-white border-gray-200'}`}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="font-bold flex items-center gap-2">
                <Trophy size={18} /> Engajamento
              </h3>
              {engagementError ? (
                <p className={`text-xs mt-1 ${darkMode ? 'text-red-300' : 'text-red-600'}`}>
                  {engagementError}
                </p>
              ) : engagementLoading ? (
                <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                  Carregando engajamento...
                </p>
                ) : (
                  <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                    Periodo: {engagementSnapshot?.range?.from || '--'} a {engagementSnapshot?.range?.to || '--'}
                  </p>
                )}
              </div>
            </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
            {stats.map((item) => (
              <div
                key={item.id}
                className={`rounded-lg border p-3 ${darkMode ? 'border-vision-border bg-vision-surface/40' : 'border-gray-200 bg-gray-50'}`}
              >
                <p className={`text-xs font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {item.label}
                </p>
                <div className="mt-2 flex items-end gap-2">
                  <span className="text-2xl font-bold">{item.value}</span>
                </div>
                {item.helper ? (
                  <p className={`text-[11px] mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>{item.helper}</p>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className={`p-6 md:p-8 min-h-full animate-in fade-in duration-500 ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
      <div className={`relative rounded-2xl p-6 md:p-8 mb-8 overflow-hidden border ${darkMode ? 'bg-vision-darkGreen border-vision-border' : 'bg-white border-gray-200'}`}>
        <div className={`absolute inset-0 ${darkMode ? 'bg-[radial-gradient(circle_at_top,_rgba(88,140,88,0.2),_transparent_60%)]' : 'bg-[radial-gradient(circle_at_top,_rgba(41,75,41,0.12),_transparent_60%)]'}`} />
        <div className="relative flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-vision-green'}`}>
              Dashboard {role === 'admin' ? 'Executivo' : role === 'staff' ? 'Comercial' : 'Cliente'}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={openDateControls}
              className={`flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] transition-colors ${
                darkMode
                  ? 'border-vision-border text-gray-200 hover:bg-vision-surface'
                  : 'border-gray-200 text-gray-700 hover:bg-gray-100'
              }`}
              title="Definir periodo"
              aria-label="Definir periodo"
            >
              <Calendar size={14} />
              <span className="font-semibold">Periodo</span>
              <span className={`${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {currentPresetLabel} - {widgetDateFrom} a {widgetDateTo}
              </span>
            </button>
            <button
              type="button"
              onClick={() => setCardSettingsOpen(true)}
              className={`p-2 rounded-lg border transition-colors ${
                darkMode
                  ? 'border-vision-border text-gray-200 hover:bg-vision-surface'
                  : 'border-gray-200 text-gray-700 hover:bg-gray-100'
              }`}
              title="Configurar cards"
              aria-label="Configurar cards"
            >
              <Settings size={16} />
            </button>
          </div>
        </div>
      </div>

      {!snapshotCardsReady ? (
        <div className={`p-10 rounded-xl border text-center ${darkMode ? 'bg-vision-darkGreen border-vision-border text-gray-400' : 'bg-white border-gray-200 text-gray-500'}`}>
          <span className="inline-flex items-center justify-center gap-2">
            Carregando dados
            <span className="inline-flex items-center gap-1">
              <span className="h-1.5 w-1.5 rounded-full bg-current animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="h-1.5 w-1.5 rounded-full bg-current animate-bounce" style={{ animationDelay: '120ms' }} />
              <span className="h-1.5 w-1.5 rounded-full bg-current animate-bounce" style={{ animationDelay: '240ms' }} />
            </span>
          </span>
        </div>
      ) : generalWidgetOrder.length === 0 ? (
        <div className={`p-10 rounded-xl border text-center ${darkMode ? 'bg-vision-darkGreen border-vision-border text-gray-400' : 'bg-white border-gray-200 text-gray-500'}`}>
          Nenhum card ativo. Abra "Configurar cards" para reativar.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {generalWidgetOrder.map((key) => renderGeneralWidget(key))}
        </div>
      )}

      {cardSettingsOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className={`w-full max-w-xl rounded-2xl border shadow-xl ${darkMode ? 'bg-vision-darkGreen border-vision-border text-gray-100' : 'bg-white border-gray-200 text-gray-900'}`}>
            <div className={`flex items-center justify-between gap-4 p-4 border-b ${darkMode ? 'border-vision-border' : 'border-gray-200'}`}>
              <div>
                <p className="text-sm font-semibold">Cards do dashboard</p>
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Clique para ativar/desativar e arraste para reordenar.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setCardSettingsOpen(false)}
                className={`px-3 py-2 rounded-lg text-xs font-semibold border transition-colors ${
                  darkMode
                    ? 'border-vision-border text-gray-200 hover:bg-vision-surface'
                    : 'border-gray-200 text-gray-700 hover:bg-gray-100'
                }`}
                aria-label="Fechar configuracao de cards"
              >
                <X size={14} />
              </button>
            </div>
            <div className="p-4 flex flex-col gap-3">
              {!snapshotCardsReady ? (
                <div className={`rounded-lg border px-3 py-4 text-sm text-center ${darkMode ? 'border-vision-border text-gray-400 bg-vision-surface/30' : 'border-gray-200 text-gray-500 bg-gray-50'}`}>
                  Carregando configuracao dos cards...
                </div>
              ) : (
                orderedSnapshotWidgets.map((card) => {
                  const enabled = isSnapshotCardEnabled(card.id);
                  const isDragging = draggingCardId === card.id;
                  const isDragOver = dragOverCardId === card.id && draggingCardId !== card.id;
                  return (
                    <button
                      key={card.id}
                      type="button"
                      onClick={() => {
                        if (!draggingCard) {
                          toggleCard(card.id);
                        }
                      }}
                      draggable
                      onDragStart={(event) => {
                        event.dataTransfer.effectAllowed = 'move';
                        setDraggingCardId(card.id);
                        setDraggingCard(true);
                      }}
                      onDragEnd={() => {
                        setDraggingCardId(null);
                        setDragOverCardId(null);
                        setDraggingCard(false);
                      }}
                      onDragOver={(event) => {
                        event.preventDefault();
                        if (dragOverCardId !== card.id) {
                          setDragOverCardId(card.id);
                        }
                      }}
                      onDragLeave={() => {
                        if (dragOverCardId === card.id) {
                          setDragOverCardId(null);
                        }
                      }}
                      onDrop={() => {
                        if (draggingCardId) {
                          moveCard(draggingCardId, card.id);
                        }
                        setDragOverCardId(null);
                      }}
                      className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-3 text-sm transition-colors ${
                        darkMode ? 'border-vision-border text-gray-100' : 'border-gray-200 text-gray-800'
                      } ${
                        enabled
                          ? darkMode
                            ? 'bg-vision-surface/60'
                            : 'bg-gray-50'
                          : darkMode
                            ? 'bg-vision-darkGreen/40 text-gray-500'
                            : 'bg-white text-gray-400'
                      } ${isDragging ? 'opacity-60' : ''} ${isDragOver ? (darkMode ? 'border-vision-activeRed' : 'border-[#294b29]') : ''}`}
                    >
                      <div className="flex items-center gap-3">
                        <GripVertical size={16} />
                        <div className="text-left">
                          <p className="font-semibold">{card.title}</p>
                          <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{card.subtitle}</p>
                        </div>
                      </div>
                      <span
                        className={`text-[11px] font-semibold px-2 py-1 rounded-full border ${
                          enabled
                            ? darkMode
                              ? 'border-green-400 text-green-300'
                              : 'border-green-500 text-green-600'
                            : darkMode
                              ? 'border-vision-border text-gray-500'
                              : 'border-gray-200 text-gray-400'
                        }`}
                      >
                        {enabled ? 'Ativo' : 'Oculto'}
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
      {renderWidgetDateControls()}
    </div>
  );
};

